import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Plus, Pencil, Trash2, Upload, RefreshCw, BarChart3, Sparkles, X, ExternalLink } from 'lucide-react';

interface Article {
  _id: string;
  title: string;
  slug: string;
  content: string;
  publishedAt: string;
  blogPostSlug?: string;
  analysis?: {
    primaryMessage: string;
    painPoints: string[];
    audienceType: string;
    keywords: string[];
    sentiment: string;
    contentType: string;
    engagementPotential: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
}

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const INPUT = "w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50 focus:bg-white/[0.07] transition-all";
const BTN_PRIMARY = "inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_SECONDARY = "inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50";

function ArticlesManager() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [formData, setFormData] = useState({ title: '', slug: '', content: '', blogPostSlug: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);

  useEffect(() => { fetchArticles(); }, []);

  const fetchBlogPosts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const r = await fetch('/api/blog/admin/posts', { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setBlogPosts(d.posts || []); }
    } catch { /* non-critical */ }
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/articles', { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) { const data = await response.json(); setArticles(data.articles || []); }
    } catch (e) { console.error(e); setError('Failed to load articles'); }
    finally { setLoading(false); }
  };

  const handleOpenDialog = (article?: Article) => {
    if (article) { setEditingArticle(article); setFormData({ title: article.title, slug: article.slug, content: article.content, blogPostSlug: article.blogPostSlug || '' }); }
    else { setEditingArticle(null); setFormData({ title: '', slug: '', content: '', blogPostSlug: '' }); }
    setOpen(true); setError(''); setSuccess('');
    fetchBlogPosts();
  };

  const handleCloseDialog = () => {
    setOpen(false); setEditingArticle(null);
    setFormData({ title: '', slug: '', content: '', blogPostSlug: '' }); setError(''); setSuccess('');
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) { setError('Title and content are required'); return; }
    setSubmitting(true); setError('');
    try {
      const url = editingArticle ? `/api/articles/${editingArticle._id}` : '/api/articles';
      const method = editingArticle ? 'PUT' : 'POST';
      const token = localStorage.getItem('adminToken');
      const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(formData) });
      if (response.ok) { setSuccess(editingArticle ? 'Article updated!' : 'Article created!'); handleCloseDialog(); fetchArticles(); }
      else { const d = await response.json(); setError(d.message || 'Failed to save article'); }
    } catch (e) { console.error(e); setError('Failed to save article'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this article?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const r = await fetch(`/api/articles/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { setSuccess('Deleted!'); fetchArticles(); } else setError('Failed to delete');
    } catch (e) { console.error(e); setError('Failed to delete'); }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      setSubmitting(true);
      const token = localStorage.getItem('adminToken');
      const r = await fetch('/api/articles/import', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (r.ok) { setSuccess('Imported!'); fetchArticles(); } else { const d = await r.json(); setError(d.message || 'Failed to import'); }
    } catch (e) { console.error(e); setError('Failed to import'); }
    finally { setSubmitting(false); }
  };

  const handleAnalyze = async (id: string, title: string) => {
    if (!confirm(`Analyze: "${title}"?`)) return;
    try {
      setSubmitting(true);
      const token = localStorage.getItem('adminToken');
      const r = await fetch(`/api/articles/${id}/analyze`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { setSuccess(`"${title}" analyzed!`); fetchArticles(); } else { const d = await r.json(); setError(d.message || 'Failed'); }
    } catch (e) { console.error(e); setError('Failed to analyze'); }
    finally { setSubmitting(false); }
  };

  const handleGenerateContent = async (id: string, title: string) => {
    if (!confirm(`Generate content for: "${title}"?`)) return;
    try {
      setSubmitting(true);
      const token = localStorage.getItem('adminToken');
      const r = await fetch(`/api/content/articles/${id}/generate-content`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ platforms: ['twitter', 'linkedin', 'instagram', 'facebook', 'tiktok', 'reddit', 'youtube_shorts', 'email', 'push'], forceRegenerate: true })
      });
      if (r.ok) { const d = await r.json(); setSuccess(`Generated ${d.result.successful} posts!`); fetchArticles(); }
      else { const d = await r.json(); setError(d.message || 'Failed'); }
    } catch (e) { console.error(e); setError('Failed to generate'); }
    finally { setSubmitting(false); }
  };

  const engagementBadge = (ep?: string) => {
    if (!ep) return null;
    const cls = ep === 'high' ? 'bg-green/15 text-green' : ep === 'medium' ? 'bg-orange/15 text-orange' : 'bg-white/10 text-white/40';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{ep}</span>;
  };

  const statsCards = [
    { label: 'Total Articles', value: articles.length },
    { label: 'Analyzed', value: articles.filter(a => a.analysis).length },
    { label: 'High Engagement', value: articles.filter(a => a.analysis?.engagementPotential === 'high').length },
    { label: 'This Week', value: articles.filter(a => { const w = new Date(); w.setDate(w.getDate()-7); return new Date(a.createdAt) > w; }).length },
  ];

  if (loading) {
    return <div className="flex items-center justify-center py-24"><div className="w-7 h-7 border-2 border-white/10 border-t-primary rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">Marketing Articles Manager</h1>
        <div className="flex flex-wrap gap-2">
          <button onClick={fetchArticles} className={BTN_SECONDARY} disabled={loading}><RefreshCw size={14} /> Refresh</button>
          <label className={`${BTN_SECONDARY} cursor-pointer`}>
            <Upload size={14} /> Import
            <input type="file" className="hidden" accept=".json,.csv" onChange={handleFileUpload} disabled={submitting} />
          </label>
          <button onClick={() => handleOpenDialog()} className={BTN_PRIMARY}><Plus size={14} /> Add Article</button>
        </div>
      </div>

      {error && <div className="bg-red/10 border border-red/20 text-red rounded-lg px-4 py-3 text-sm">{error}</div>}
      {success && <div className="bg-green/10 border border-green/20 text-green rounded-lg px-4 py-3 text-sm">{success}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {statsCards.map((s, i) => (
          <div key={i} className={`${CARD} p-4`}>
            <p className="text-white/40 text-xs mb-1">{s.label}</p>
            <p className="text-2xl font-bold text-white">{s.value}</p>
          </div>
        ))}
      </div>

      <div className={CARD}>
        {articles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-white/40 text-sm">No articles found</p>
            <p className="text-white/25 text-xs mt-1">Create your first article to start AI content generation.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {['Title', 'Slug', 'Analysis', 'Engagement', 'Published', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a._id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 max-w-[250px]">
                      <p className="text-white/80 text-xs truncate">{a.title}</p>
                      {a.blogPostSlug && (
                        <a href={`/blog/${a.blogPostSlug}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-primary text-[10px] hover:text-primary/80 transition-colors mt-0.5">
                          <ExternalLink size={9} /> View Blog Post
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3"><code className="text-white/40 text-xs bg-white/[0.05] px-1.5 py-0.5 rounded">{a.slug}</code></td>
                    <td className="px-4 py-3">
                      {a.analysis ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green/15 text-green">Analyzed</span>
                        : <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange/15 text-orange">Pending</span>}
                    </td>
                    <td className="px-4 py-3">{engagementBadge(a.analysis?.engagementPotential)}</td>
                    <td className="px-4 py-3 text-white/40 text-xs">{new Date(a.publishedAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => handleAnalyze(a._id, a.title)} disabled={submitting} title="Analyze"
                          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${a.analysis ? 'bg-orange/25 hover:bg-orange/35 text-orange' : 'bg-primary/25 hover:bg-primary/35 text-primary'}`}>
                          <BarChart3 size={13} />
                        </button>
                        {a.analysis && (
                          <button onClick={() => handleGenerateContent(a._id, a.title)} disabled={submitting} title="Generate"
                            className="w-7 h-7 rounded-lg bg-purple/25 hover:bg-purple/35 text-purple flex items-center justify-center transition-colors">
                            <Sparkles size={13} />
                          </button>
                        )}
                        <button onClick={() => handleOpenDialog(a)} disabled={submitting} title="Edit"
                          className="w-7 h-7 rounded-lg bg-primary/25 hover:bg-primary/35 text-primary flex items-center justify-center transition-colors">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => handleDelete(a._id)} disabled={submitting} title="Delete"
                          className="w-7 h-7 rounded-lg bg-red/25 hover:bg-red/35 text-red flex items-center justify-center transition-colors">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog.Root open={open} onOpenChange={(v) => !v && handleCloseDialog()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111113] border border-white/[0.08] rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <Dialog.Title className="text-base font-semibold text-white">
                {editingArticle ? 'Edit Article' : 'Add New Article'}
              </Dialog.Title>
              <Dialog.Close className="text-white/40 hover:text-white transition-colors"><X size={18} /></Dialog.Close>
            </div>
            {error && <div className="bg-red/10 border border-red/20 text-red rounded-lg px-4 py-3 text-sm mb-4">{error}</div>}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Title *</label>
                <input type="text" className={INPUT} value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Slug</label>
                <input type="text" className={INPUT} value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} placeholder="URL-friendly identifier" />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Content *</label>
                <textarea className={INPUT} value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} rows={10} placeholder="Article content in HTML or Markdown" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Linked Blog Post <span className="text-white/25">(optional)</span></label>
                <select className={INPUT} value={formData.blogPostSlug} onChange={(e) => setFormData({ ...formData, blogPostSlug: e.target.value })}>
                  <option value="">— No blog post linked —</option>
                  {blogPosts.map(p => (
                    <option key={p._id} value={p.slug}>{p.title} ({p.slug})</option>
                  ))}
                </select>
                <p className="text-white/25 text-xs mt-1">Link this marketing article to its published blog post to enable "View Blog Post →" in Content Viewer.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-white/[0.06]">
              <button onClick={handleCloseDialog} disabled={submitting} className={BTN_SECONDARY}>Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} className={BTN_PRIMARY}>
                {submitting ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> Saving…</> : (editingArticle ? 'Update' : 'Create')}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

export default ArticlesManager;
